import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeroSectionProps {
  onConsultationClick: () => void;
}

const HeroSection = ({ onConsultationClick }: HeroSectionProps) => {
  return (
    <section className="relative bg-gradient-to-br from-primary via-secondary to-primary/90 text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container mx-auto px-4 py-20 lg:py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm font-cta font-semibold">Trusted by 500+ Businesses</span>
            </div>

            {/* Main Headline */}
            <h1 className="font-headline font-bold text-4xl lg:text-6xl leading-tight">
              Your Strategic Technology Partner for Business Growth
            </h1>

            {/* Subheadline */}
            <p className="text-xl lg:text-2xl text-white/90 font-body">
              Enterprise-level IT expertise meets personalized service. We transform technology challenges into competitive advantages.
            </p>

            {/* Value Props */}
            <div className="grid sm:grid-cols-3 gap-4">
              {[
              { icon: 'ClockIcon', label: '24/7 Support' },
              { icon: 'ShieldCheckIcon', label: 'Certified Experts' },
              { icon: 'ChartBarIcon', label: '99.9% Uptime' }].
              map((item, index) =>
              <div key={index} className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg">
                  <Icon name={item.icon as any} size={24} variant="outline" className="text-accent" />
                  <span className="font-body font-semibold">{item.label}</span>
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={onConsultationClick}
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-accent hover:bg-accent/90 text-white rounded-lg font-cta font-bold text-lg shadow-elevated hover:shadow-card transition-all duration-300 transform hover:scale-105">

                <Icon name="CalendarIcon" size={24} variant="outline" />
                <span>Get Free IT Assessment</span>
              </button>
              <Link
                href="/services-overview"
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg font-cta font-semibold text-lg border-2 border-white/30 transition-all duration-300">

                <span>Explore Services</span>
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
              </Link>
            </div>

            {/* Emergency Support Banner */}
            <div className="flex items-center space-x-3 bg-error/20 backdrop-blur-sm px-6 py-4 rounded-lg border border-error/30">
              <div className="w-3 h-3 bg-error rounded-full animate-pulse-slow" />
              <div className="flex-1">
                <p className="font-cta font-bold text-sm">Emergency IT Support Available Now</p>
                <p className="text-sm text-white/80">Response time under 15 minutes</p>
              </div>
              <Link
                href="/contact"
                className="flex items-center space-x-1 px-4 py-2 bg-error hover:bg-error/90 rounded-md font-cta font-semibold text-sm transition-colors duration-200">

                <Icon name="PhoneIcon" size={18} variant="solid" />
                <span>Call Now</span>
              </Link>
            </div>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-elevated">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1c81d94bf-1769278367150.png"
                alt="Professional IT team collaborating in modern office with multiple monitors displaying network infrastructure and security dashboards"
                className="w-full h-[500px] object-cover" />

              {/* Floating Stats Cards */}
              <div className="absolute top-8 right-8 bg-white rounded-lg shadow-card p-4 backdrop-blur-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircleIcon" size={28} variant="solid" className="text-success" />
                  </div>
                  <div>
                    <p className="font-headline font-bold text-2xl text-primary">99.9%</p>
                    <p className="text-sm text-muted-foreground font-body">System Uptime</p>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-8 left-8 bg-white rounded-lg shadow-card p-4 backdrop-blur-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="UserGroupIcon" size={28} variant="solid" className="text-accent" />
                  </div>
                  <div>
                    <p className="font-headline font-bold text-2xl text-primary">500+</p>
                    <p className="text-sm text-muted-foreground font-body">Happy Clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;